// Copyright (c) 2021, DIALPAD INC., all rights reserved.

import React, {useEffect, useState} from 'react';
import {Card} from '@app/components/Card/Card';
import {DropDown} from '@app/components/inputs/dropdown/DropDown';
import {MdRateReview, MdSpeakerNotesOff} from 'react-icons/md';
import {FeedbackList} from './feedback-list';
import {FeedbackViewer} from './feedback-viewer';
import {useDispatch, useSelector} from 'react-redux';
import {
    areAspectFeedbackMessagesFetchingSelector,
    getSelectedMessage,
    getFeedbackMessagesByMetricId,
    getSelectedMetricId,
    getSelectedAspect,
} from '@app/redux/feedback/selectors';
import {
    changeSelectedMessage,
    changeSelectedMetricId,
} from '@app/redux/feedback/actions';

/* Main component that constructs the Feedback messages list & viewer.
 * */
export const FeedbackListViewer = (props) => {
    const {cxType, handleClick, messages} = props;
    const filteredMessages = useSelector((state) =>
        getFeedbackMessagesByMetricId(state),
    );
    const selectedMetricId = useSelector((state) => getSelectedMetricId(state));
    const selectedMessage = useSelector((state) => getSelectedMessage(state));
    const selectedAspect = useSelector((state) => getSelectedAspect(state));
    const isRefreshing = useSelector((state) =>
        areAspectFeedbackMessagesFetchingSelector(state),
    );

    const dispatch = useDispatch();

    const [options, setOptions] = useState<any>();
    const [selectedDomain, setSelectedDomain] = useState<any>();
    const [question, setQuestion] = useState('');

    // When the aspect is changed reload the dropdown options to reflect the correct ones.
    useEffect(() => {
        if (!isRefreshing)
            setOptions(
                [...new Set(messages.map((msg) => msg?.body?.metric_id))].map(
                    (msg, index) => ({
                        value: index,
                        label: msg,
                    }),
                ),
            );
    }, [selectedAspect, isRefreshing]);

    // When the selected message is changed, change the properties so that they display the correct info.
    useEffect(() => {
        if (!isRefreshing && selectedMessage) {
            setQuestion(selectedMessage.body.question);
        }
    }, [selectedMessage, isRefreshing]);

    // When the metricId is changed change the selected message to the first message.
    useEffect(() => {
        if (!isRefreshing && filteredMessages.length) {
            dispatch(changeSelectedMessage(filteredMessages[0]));
        }
    }, [selectedMetricId, isRefreshing]);

    // When the dropdown options are changed make sure the selected metricId item is the same as the selected message's metricId
    useEffect(() => {
        if (options && options.length) {
            setSelectedDomain(
                options.find(
                    (a) => a.label === selectedMessage?.body?.metric_id,
                ),
            );
        }
    }, [options]);

    // When the domain changes from the dropdown trigger the metricId Change.
    const onDomainChanged = (domainValue) => {
        setSelectedDomain(domainValue);
        dispatch(changeSelectedMetricId(domainValue.label));
    };

    return (filteredMessages.length && options && options.length) ||
        isRefreshing ? (
        <Card>
            <div className="feedback-list-viewer-wrapper">
                <div className="feedback-list-viewer-header">
                    {!isRefreshing && (
                        <React.Fragment>
                            <div className="feedback-list-viewer-dropdown">
                                {selectedDomain && (
                                    <DropDown
                                        value={{
                                            label: selectedDomain.label,
                                            value: selectedDomain.value,
                                        }}
                                        options={options}
                                        onChange={(v) => {
                                            onDomainChanged(v);
                                        }}
                                    />
                                )}
                            </div>
                            <div className="feedback-list-viewer-question">
                                <span>{question}</span>
                            </div>
                            <div className="feedback-list-viewer-feedback-count">
                                <div className="feedback-list-viewer-icon">
                                    <MdRateReview size={24} />
                                </div>
                                <div className="feedback-list-viewer-counter">
                                    <span className="counter">
                                        {filteredMessages.length}
                                    </span>
                                    <span className="label">FEEDBACK</span>
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </div>
                <div className="feedback-list-viewer-body">
                    <FeedbackList
                        cxType={cxType}
                        list={filteredMessages}
                        handleClick={handleClick}
                        selectedMessage={selectedMessage}
                    />
                    <FeedbackViewer
                        cxType={cxType}
                        selectedMessage={selectedMessage}
                    />
                </div>
            </div>
        </Card>
    ) : (
        <Card>
            <span className="no-results">
                <span className="no-results-icon">
                    <MdSpeakerNotesOff size={50} />
                </span>
                <span className="no-results-label">
                    <h1>No messages found</h1>
                </span>
            </span>
        </Card>
    );
};
